<template>
    <div class="items-in-return-table">
        <rothco-table
            :pt="{
                tableContainer: {
                    style: {
                        overflow:'unset'
                    }
                }
            }"
            class="table"
            key="table"
            v-model:content="tableContent"
            :checkable="checkable"
            :checkableOnMobile="true"
            v-model:checkedRows="checkedRows"
            primaryKey="order_item_id"
            :rowIsCheckable="rowIsCheckable"
            :visible-fields="visibleFields">

            <template v-for="(item, index) in items" :key="`item_image_${index}`" v-slot:[`item_image_${index}`]="slotProps">
                <div class="img-background">
                    <img :src="item.image" :srcset="item.image_srcset" :sizes="item.image_sizes" alt="Product name">
                </div>
            </template>

            <template v-for="(item, index) in items" :key="`item_info_${index}`" v-slot:[`item_info_${index}`]="slotProps">
                <Link class="table-name paragraph-default" :href="route('productDetail.show', [item.seo_name])">
                    <h2 class="product_row-title title-s">{{ item.item_name }}</h2>
                </Link>
                <p class="product_row-id paragraph-details">#{{ item.item_no }}</p>
                <p class="product_row-size paragraph-details">{{ item.variants_text }}</p>
                <p class="product_row-id paragraph-details price-qty"><span>Price: ${{ parseFloat(item.price).toFixed(2) }}</span><span>Quantity: {{item.quantity}}</span></p>
            </template>
            
            <template v-for="(item, index) in items" :key="`return_quantity_${index}`" v-slot:[`return_quantity_${index}`]="slotProps">
                
                <span v-if="isReturnSuccess">
                    {{item.return_quantity}}
                </span>
                
                <span v-else>
                    <dt v-if="item.price <= 0">Item is not eligible to return</dt>
                    <dt v-else-if="!item.returnable_quantity">Item has already returned</dt>
                    <div v-else class="field-state input-folder">
                             <div class="input-folder--input">
                                <PrimeInputNumber
                                    v-model="item.return_quantity"
                                    type="text" 
                                    :class="{ 'has-number': item.return_quantity }"
                                    id="return_quantity" 
                                    name="return_quantity" 
                                    placeholder="" 
                                    :readonly="!item.selected_to_return"
                                    autocomplete="false"
                                    :max="item.returnable_quantity"
                                />
                                <label for="return_quantity">
                                    <p class="paragraph-default">Enter quantity</p>
                                </label>
                            </div>
                            <p class="input-support-text invalid-state" v-if="errors[`items[${index}].return_quantity`]">{{errors[`items[${index}].return_quantity`]}}</p>
                    </div>
                </span>
            </template>

            <template v-for="(item, index) in items" :key="`return_reason_${index}`" v-slot:[`return_reason_${index}`]="slotProps">
                <span v-if="isReturnSuccess">
                    {{getReturnReasonByKey(item.return_reason_id)}}
                </span>
                <span v-else>
                    <custom-select 
                        v-if="item.returnable_quantity && item.price > 0"
                        name="return_reason" 
                        id="return_reason" 
                        label="Select reason"
                        aria-label="Select return reason" 
                        :options="formattedReturnReasons"
                        :class="{ error: !item.return_reason && item.return_quantity}"
                        v-model="returnReasons[index].value"
                        :readonly="!item.selected_to_return"
                    />
                    <p class="return-reason-error input-support-text invalid-state" v-if="errors[`items[${index}].return_reason`]">{{errors[`items[${index}].return_reason`]}}</p>
                </span>
            </template>
        </rothco-table>
    </div>
</template>

<script setup lang="ts">
    import { computed, toRefs, watch, ref} from "vue";
    import { route } from "ziggy-js";
    import { Link } from "@inertiajs/vue3";
    import RothcoTable from "@/components/RothcoTable.vue";

    const props = defineProps({
        availableReturnReasons: {
            type: Object,
            default: {},
            required: false,
        },
        checkable: {
            type: Boolean,
            default: false
        },
        isReturnSuccess: {
            type: Boolean,
            default: false
        },
        errors: {
            type: Object,
            default: {},
            required: false,
        }
    })

    const { availableReturnReasons, errors } = toRefs(props);

    const items = defineModel({
        type: Array,
    });

    let checkedRows = ref([]);

    const tableContent = computed(()=>{
        return items.value.map(list=>{
            return {    
                data: {
                    ...list
                }
            }       
        });             
    });

    watch(checkedRows, () => {
        items.value = items.value.map((item) => {
            const isChecked = checkedRows.value.some(row => row.data.order_item_id == item.order_item_id)
            item.selected_to_return = isChecked;
            return { ...item };
        });
    }, { deep: true });

    const returnReasons = computed(() =>
        items.value.map((item) =>
            computed({
                get() {
                    return {
                        value: formattedReturnReasons.value.find(reason => reason.key == item.return_reason)?.value ?? "",
                        key: item.return_reason,
                    };
                },
                set(newVal) {
                    item.return_reason = newVal.key;
                },
            })
        )
    );

    const formattedReturnReasons = computed(() => {
        return Object.entries(availableReturnReasons.value).map(([key, name]) => {
            return {    
                key: key, 
                value: name
            };
        });
    });

    const rowIsCheckable = (item) => {
        return item.returnable_quantity > 0 && item.price > 0;
    }

    const isItemSelectedToReturn = (order_item_id) => {
        return checkedRows.value.some(row => row.data.order_item_id == order_item_id);
    }
    
    const getReturnReasonByKey = (reason_id) => {
        return formattedReturnReasons.value.find((reason) => reason.key == reason_id)?.value;
    }

    const visibleFields = computed(()=>{
        return [
            {
                label: "",
                field: "item_image",
                is_custom: true,
                show_only: 'desktop',
                pt: {
                    headerCell: {},
                    bodyCell: { class: 'desktop-only desktop-image' }
                }
            },
            {
                label: "Item Info",
                field: "item_info",
                is_custom: true,
                pt: {
                    headerCell: { class: 'title-xs text-left' },
                    bodyCell: (state)=>{
                        const isReturnSuccess = state.instance.rowData.data.return_item_id;
                        return {
                            class: `text-left ${isReturnSuccess ? 'mob-w-50' : 'mob-w-75'}`,
                        };
                    }
                }
                
            },
            {
                label: "Return Qty",
                field: "return_quantity",
                is_custom: true,
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: (state)=>{
                        const isReturnSuccess = state.instance.rowData.data.return_item_id;
                        return { 
                            class: `return-quantity desk-text-right ${isReturnSuccess ? 'mob-w-25' : 'mob-w-50'}`, 
                            'data-label': "Return Qty"
                        }
                    }
                }
            },
            {
                label: "Return Reason",
                field: "return_reason",
                is_custom: true,
                pt: {
                    headerCell: { class: 'text-right' },
                    bodyCell: (state)=> {
                        const isReturnSuccess = state.instance.rowData.data.return_item_id;
                        return { 
                            class: `desk-text-right ${isReturnSuccess ? 'mob-w-25' : 'mob-w-50'}`, 
                            'data-label': "Return Reason"
                        }
                    }
                }
            },
        ]
    })
</script>

